import { DEFAULT_TIME_ZONE, dbDateTimeToUtcMs, normalizeTimeZone, zonedDateTimeToUtcMs } from './timezone';

const ACTIVE_STATUSES = new Set([
  'created',
  'confirmed',
  'accepted',
  'in_progress',
]);

const HISTORY_STATUSES = new Set([
  'completed',
  'cancelled',
  'no_show',
  'rejected',
  'declined',
]);

export function normalizeStatus(status) {
  const value = String(status || '').trim().toLowerCase();

  const map = {
    created: 'created',
    confirmed: 'confirmed',
    accepted: 'accepted',
    in_progress: 'in_progress',
    completed: 'completed',
    cancelled: 'cancelled',
    no_show: 'no_show',
    rejected: 'rejected',
    declined: 'declined',

    'создана': 'created',
    'подтверждена': 'confirmed',
    'принято': 'accepted',
    'принята': 'accepted',
    'в процессе': 'in_progress',
    'завершено': 'completed',
    'завершена': 'completed',
    'отменено': 'cancelled',
    'отменена': 'cancelled',
    'не пришёл': 'no_show',
    'не пришел': 'no_show',
    'отклонено': 'rejected',
    'отклонена': 'rejected',
  };

  return map[value] || value.replace(/\s+/g, '_');
}

function getOrderTimeZone(order, fallbackTimeZone = DEFAULT_TIME_ZONE) {
  return normalizeTimeZone(
    (order && (order.club_timezone || order.timezone || (order.club && order.club.timezone))),
    fallbackTimeZone
  );
}

function parseDateTimeMs(dateValue, timeValue = '00:00:00', timeZone = DEFAULT_TIME_ZONE) {
  if (!dateValue) return null;

  const value = String(dateValue || '').trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return zonedDateTimeToUtcMs(value, timeValue, timeZone);
  }

  if (/^\d{4}-\d{2}-\d{2}[ T]\d{1,2}:\d{2}(:\d{2})?$/.test(value)) {
    return dbDateTimeToUtcMs(value, timeZone);
  }

  // Если сервер уже отдал ISO со смещением/Z, считаем это абсолютным временем.
  // Такое бывает, когда MySQL DATETIME сериализуется через JSON как Date.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})$/.test(value)) {
    const nativeDate = new Date(value);
    return Number.isNaN(nativeDate.getTime()) ? null : nativeDate.getTime();
  }

  const nativeDate = new Date(value);
  return Number.isNaN(nativeDate.getTime()) ? null : nativeDate.getTime();
}

export function getOrderTimestamp(order, fallbackTimeZone = DEFAULT_TIME_ZONE, bound = 'start') {
  if (!order || typeof order !== 'object') return 0;

  const timeZone = getOrderTimeZone(order, fallbackTimeZone);
  const directValue = bound === 'end' ? order.booking_end_at : order.booking_start_at;
  const directMs = directValue ? parseDateTimeMs(directValue, '00:00:00', timeZone) : null;
  if (Number.isFinite(directMs) && directMs > 0) return directMs;

  const dateMs = (
    parseDateTimeMs(order.booking_date, bound === 'end' ? (order.end_time || order.start_time) : order.start_time, timeZone) ||
    parseDateTimeMs(order.visit_date, order.start_time, timeZone) ||
    parseDateTimeMs(order.deadline_date, order.start_time, timeZone) ||
    0
  );

  if (bound === 'end' && (!dateMs || dateMs === getOrderTimestamp(order, fallbackTimeZone, 'start'))) {
    const startMs = getOrderTimestamp(order, fallbackTimeZone, 'start');
    const durationMins = Number(order.duration_mins || Number(order.hours_count || 0) * 60 || 0);
    if (startMs > 0 && durationMins > 0) {
      return startMs + durationMins * 60 * 1000;
    }
  }

  return dateMs || parseDateTimeMs(order.created_at, '00:00:00', timeZone) || 0;
}

function getOrderDateMs(order, fallbackTimeZone = DEFAULT_TIME_ZONE, bound = 'start') {
  return getOrderTimestamp(order, fallbackTimeZone, bound);
}

export function getOrderDate(order, fallbackTimeZone = DEFAULT_TIME_ZONE) {
  return new Date(getOrderDateMs(order, fallbackTimeZone, 'start'));
}

export function getOrderEndDate(order, fallbackTimeZone = DEFAULT_TIME_ZONE) {
  return new Date(getOrderDateMs(order, fallbackTimeZone, 'end'));
}

export function isHistoryBooking(order, now = Date.now(), fallbackTimeZone = DEFAULT_TIME_ZONE) {
  if (!order || typeof order !== 'object') {
    return true;
  }

  const status = normalizeStatus(order.status);
  const startMs = getOrderDateMs(order, fallbackTimeZone, 'start');
  const endMs = getOrderDateMs(order, fallbackTimeZone, 'end') || startMs;

  if (HISTORY_STATUSES.has(status)) return true;
  if (ACTIVE_STATUSES.has(status)) return endMs < now;

  return startMs < now;
}

export function isActiveBooking(order, now = Date.now(), fallbackTimeZone = DEFAULT_TIME_ZONE) {
  return !isHistoryBooking(order, now, fallbackTimeZone);
}

export function isUpcomingBooking(order, now = Date.now(), fallbackTimeZone = DEFAULT_TIME_ZONE) {
  if (!isActiveBooking(order, now, fallbackTimeZone)) return false;
  return getOrderTimestamp(order, fallbackTimeZone, 'start') >= now;
}

export function getUpcomingBookings(items = [], fallbackTimeZone = DEFAULT_TIME_ZONE) {
  const now = Date.now();
  const safeItems = Array.isArray(items) ? items.filter(Boolean) : [];

  return safeItems
    .filter((item) => isUpcomingBooking(item, now, fallbackTimeZone))
    .sort((a, b) => getOrderTimestamp(a, fallbackTimeZone, 'start') - getOrderTimestamp(b, fallbackTimeZone, 'start'));
}

export function sortBookings(items = [], fallbackTimeZone = DEFAULT_TIME_ZONE) {
  const now = Date.now();
  const safeItems = Array.isArray(items) ? items.filter(Boolean) : [];

  const active = safeItems
    .filter((item) => isActiveBooking(item, now, fallbackTimeZone))
    .sort((a, b) => getOrderTimestamp(a, fallbackTimeZone, 'start') - getOrderTimestamp(b, fallbackTimeZone, 'start'));

  const history = safeItems
    .filter((item) => isHistoryBooking(item, now, fallbackTimeZone))
    .sort((a, b) => getOrderTimestamp(b, fallbackTimeZone, 'start') - getOrderTimestamp(a, fallbackTimeZone, 'start'));

  return {
    active,
    history,
    upcoming: getUpcomingBookings(safeItems, fallbackTimeZone),
    all: [...active, ...history],
  };
}
